import {
  AuthenticationReducer,
  AuthenticationActions,
  TAuth,
} from "@Reducers/Authentication.reducer";
import { FC, memo, useReducer, useCallback, ReactNode, useEffect } from "react";
import AuthenticationContext from "@Contexts/Authentication.context";
import { setSession } from "@Utilities/Session";
import { CrmUsersViewModel } from "@Service/Models";
import secureLocalStorage from "react-secure-storage";

export interface IAuthenticationProviderProps {
  children: ReactNode;
}

const AuthenticationProvider: FC<IAuthenticationProviderProps> = memo(
  ({ children }) => {
    const [state, dispatch] = useReducer(AuthenticationReducer, {
      isLoading: true,
      attributes: null,
      isAuthenticated: false,
    } as TAuth);

    const initialize = useCallback(() => {
      const user =
        typeof window !== "undefined" ? secureLocalStorage.getItem("user") : "";
      const decodedUser = user ? JSON.parse(user as string) : null;

      if (decodedUser) {
        setSession(decodedUser);

        dispatch({
          type: AuthenticationActions.Initialize,
          payload: {
            id: decodedUser.id,
            email: decodedUser.email,
            fullName: [decodedUser.name, decodedUser.surname].join(" "),
            name: decodedUser.name,
            surname: decodedUser.surname,
            accessToken: decodedUser.accessToken,
            isActive: decodedUser.isActive,
            loginFailedCount: decodedUser.loginFailedCount,
            phoneNumber: decodedUser.phoneNumber,
            role: decodedUser.role,
          },
        });
      } else {
        logout();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(async (user: CrmUsersViewModel | undefined) => {
      const decodedToken = user;

      if (decodedToken) {
        setSession(user);

        dispatch({
          type: AuthenticationActions.SignIn,
          payload: {
            id: decodedToken.id,
            email: decodedToken.email,
            fullName: [decodedToken.name, decodedToken.surname].join(" "),
            name: decodedToken.name,
            surname: decodedToken.surname,
            accessToken: decodedToken.accessToken,
            isActive: decodedToken.isActive,
            loginFailedCount: decodedToken.loginFailedCount,
            phoneNumber: decodedToken.phoneNumber,
            role: decodedToken.role,
          },
        });
      }
    }, []);

    const logout = useCallback(() => {
      setSession(null);

      dispatch({ type: AuthenticationActions.SignOut, payload: null });
    }, []);

    const restoreToken = useCallback(() => {
      dispatch({ type: AuthenticationActions.RestoreToken, payload: null });
    }, []);

    useEffect(() => {
      if (!state.isAuthenticated) initialize();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <AuthenticationContext.Provider
        value={{ ...state, login, logout, restoreToken, initialize }}
      >
        {children}
      </AuthenticationContext.Provider>
    );
  }
);

export default AuthenticationProvider;
